import { move } from "@dnd-kit/helpers";
import { DragDropProvider } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";

const compareNames = (a, b) =>
  a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

// Inline SVGs so the icons follow the text color in both themes.
export const SortIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 2.5v11M2.5 5 5 2.5 7.5 5M11 13.5v-11M8.5 11 11 13.5 13.5 11" />
  </svg>
);

const GripIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="currentColor"
    aria-hidden="true"
  >
    {[3, 7, 11].map((cy) => (
      <g key={cy}>
        <circle cx="4.5" cy={cy} r="1.2" />
        <circle cx="9.5" cy={cy} r="1.2" />
      </g>
    ))}
  </svg>
);

const CrossIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M4 4l8 8M12 4l-8 8" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3.5 8.5l3 3 6-7" />
  </svg>
);

const SortableGroup = ({ name, index }) => {
  const { ref, isDragging } = useSortable({ id: name, index });

  return (
    <button
      type="button"
      ref={ref}
      className="athletes-sort-groups__group"
      data-dragging={isDragging || undefined}
    >
      <GripIcon />
      {name}
    </button>
  );
};

// Inline replacement for the group tabs while the coach reorders them; the
// order only persists on Save.
const SortGroupsBar = ({ athletesGroups = [], onClose }) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const [groupNames, setGroupNames] = useState(() =>
    athletesGroups.map(({ name }) => name)
  );

  const sortBy = (direction) => {
    const sorted = [...groupNames].sort(compareNames);
    setGroupNames(direction === "asc" ? sorted : sorted.reverse());
  };

  const handleDragEnd = (event) => {
    if (event.canceled) return;
    setGroupNames((names) => move(names, event));
  };

  const saveOrder = () => {
    onClose();
    submit("/coaching-portal/athletes/sort-groups", {
      method: "POST",
      body: { groupNames },
    });
  };

  return (
    <div className="athletes-sort-groups force-mobile-hidden">
      <DragDropProvider onDragEnd={handleDragEnd}>
        {groupNames.map((name, index) => (
          <SortableGroup key={name} name={name} index={index} />
        ))}
      </DragDropProvider>
      <div className="athletes-sort-groups__controls">
        <button type="button" onClick={() => sortBy("asc")}>
          {t("athletes.header.sortAZ")}
        </button>
        <button type="button" onClick={() => sortBy("desc")}>
          {t("athletes.header.sortZA")}
        </button>
        <span className="athletes-sort-groups__divider" />
        <button
          type="button"
          className="athletes-sort-groups__icon"
          data-action="cancel"
          onClick={onClose}
          title={t("common:cancel")}
          aria-label={t("common:cancel")}
        >
          <CrossIcon />
        </button>
        <button
          type="button"
          className="athletes-sort-groups__icon"
          data-button-type="primary"
          onClick={saveOrder}
          title={t("common:save")}
          aria-label={t("common:save")}
        >
          <CheckIcon />
        </button>
      </div>
    </div>
  );
};

export default SortGroupsBar;
